/**
 * Pack a URDF and the mesh/texture files it needs into the archive the
 * upload endpoint expects.
 *
 * Split out of RegisterRobotModal.tsx: the path rewriting and the
 * de-duplication against the matched meshes are the parts worth testing, and
 * they do not need the modal to run.
 */

import JSZip from "jszip";
import type { MeshRef } from "./urdfAssets";

export interface ModuleZipInput {
  /** Directory the archive is rooted at, and the module name on the server. */
  packageName: string;
  urdfFileName: string;
  urdfContent: string;
  /** Meshes the URDF referenced; only the matched ones are packed. */
  requiredMeshes: MeshRef[];
  /** Everything the user supplied, including textures nothing referenced. */
  uploadedFiles: File[];
}

export function buildModuleZip(
  { packageName, urdfFileName, urdfContent, requiredMeshes, uploadedFiles }: ModuleZipInput,
  onProgress?: (percent: number) => void
): Promise<Blob> {
  const zip = new JSZip();

  // Add URDF
  zip.file(`${packageName}/${urdfFileName}`, urdfContent);

  // Add matched meshes and textures preserving relative paths
  for (const mesh of requiredMeshes) {
    if (mesh.matched && mesh.matchedFile) {
      zip.file(`${packageName}/${mesh.relativePath}`, mesh.matchedFile);
    }
  }

  // Also include any uploaded files that are not already in the
  // required list (e.g. textures discovered from directory upload)
  const includedPaths = new Set(
    requiredMeshes
      .filter((m) => m.matched)
      .map((m) => m.relativePath.toLowerCase()),
  );
  for (const file of uploadedFiles) {
    const relPath = file.webkitRelativePath || file.name;
    // Strip leading directory name that matches the package
    const parts = relPath.split("/");
    const innerPath = parts.length > 1 ? parts.slice(1).join("/") : relPath;
    if (!includedPaths.has(innerPath.toLowerCase())) {
      zip.file(`${packageName}/${innerPath}`, file);
      includedPaths.add(innerPath.toLowerCase());
    }
  }

  return zip.generateAsync({ type: "blob" }, (metadata) => {
    onProgress?.(Math.round(metadata.percent));
  });
}
